'use client';

import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Box, Chip, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { AppLink } from '@/components/mui/app-link';
import { ativoPath, type AtivoPortfolioSubview } from '@/libs/ativo-route';
import { PortfolioOverviewSection } from './TickerPortfolioOverviewSection';
import { PortfolioFundsSection } from './TickerPortfolioFundsSection';
import { PortfolioFinancialAssetsSection } from './TickerPortfolioFinancialAssetsSection';
import { PortfolioEquitiesSection } from './TickerPortfolioEquitiesSection';
import { PortfolioRealEstateSection } from './TickerPortfolioRealEstateSection';
import { useAppColors } from '@/styles/colors';
import { SIDEBAR_TAB_PAPER_SX, SIDEBAR_TAB_HEADER_SX, sidebarTabButtonSx } from '@/components/ui/sidebar-tabs-style';
import type { GeographicStateExposureView } from '@/libs/geographic-state-exposure-view';

type MetricKind = 'currency' | 'number' | 'percent';

export type PortfolioMetric = {
  label: string;
  value: number | null;
  kind: MetricKind;
  deltaValue?: number | null;
};

export type CompositionItem = {
  label: string;
  value: number;
  targetSection?: Exclude<SectionId, 'overview'>;
};

type RealEstateItem = {
  name: string;
  address: string | null;
  vacancyPercent: number | null;
  delinquencyPercent: number | null;
  revenuePercent: number | null;
};

type FundItem = {
  ticker: string | null;
  name: string;
  segment: string | null;
  quantity: number | null;
  value: number | null;
  portfolioPercent: number | null;
};

type FinancialAssetItem = {
  type: string | null;
  issuer: string | null;
  issuerCnpj: string | null;
  maturityDate: string | null;
  quantity: number | null;
  value: number | null;
};

type EquityItem = {
  company: string;
  type: string | null;
  quantity: number | null;
  value: number | null;
};

type PercentBreakdownItem = {
  label: string;
  value: number | null;
};

type ContractBreakdownItem = {
  label: string;
  count: number;
  percent: number;
};

type ContractItem = {
  source: string;
  contractType: string;
  characteristics: string;
};

export type PortfolioTabViewModel = {
  referenceDate: string | null;
  referenceStamps: {
    mensalAtivoPassivo: string | null;
    mensalComplemento: string | null;
    trimestralImoveis: string | null;
    trimestralAtivos: string | null;
    trimestralContratos: string | null;
  };
  sourceLabel: string;
  cnpjClasse: string | null;
  metrics: PortfolioMetric[];
  composition: CompositionItem[];
  realEstate: RealEstateItem[];
  geographicStateExposure: GeographicStateExposureView | null;
  geographicRegionExposure: GeographicStateExposureView | null;
  realEstateHistory: Array<{
    date: string;
    revenuePercentTotal: number | null;
    vacancyPercent: number | null;
    delinquencyPercent: number | null;
    occupancyPercent: number | null;
  }>;
  funds: FundItem[];
  financialAssets: FinancialAssetItem[];
  relatedEquities: EquityItem[];
  cashBreakdown: PortfolioMetric[];
  cashHistory: Array<{
    date: string;
    cash: number | null;
    liquidAssets: number | null;
    receivable: number | null;
    payable: number | null;
    payableRendimentosDistribuir: number | null;
    payableTaxaAdministracao: number | null;
    payableTaxaPerformance: number | null;
    payableOutros: number | null;
    payableTotalPassivo: number | null;
    payableUsedTotalPassivoFallback: boolean;
  }>;
  maturityBreakdown: PercentBreakdownItem[];
  indexerBreakdown: PercentBreakdownItem[];
  contractBreakdown: ContractBreakdownItem[];
  contracts: ContractItem[];
  notes: string[];
};

export type SectionId =
  | 'overview'
  | 'realEstate'
  | 'realEstateComposition'
  | 'realEstateRisk'
  | 'realEstateContracts'
  | 'funds'
  | 'financialAssets'
  | 'equities';

/**
 * Canonical mapping between the in-component section ids and the
 * shareable `/ativo/{ticker}/carteira/{subview}` slugs. Centralized here
 * so both the route parser, the navigation, and the overview composition
 * "Ver" buttons stay in sync.
 */
const SECTION_TO_SUBVIEW: Record<SectionId, AtivoPortfolioSubview> = {
  overview: 'visao-geral',
  realEstate: 'imoveis-composicao',
  realEstateComposition: 'imoveis-composicao',
  realEstateRisk: 'imoveis-risco',
  realEstateContracts: 'imoveis-contratos',
  funds: 'fundos-investidos',
  financialAssets: 'ativos-financeiros',
  equities: 'acoes-participacoes',
};

const SUBVIEW_TO_SECTION: ReadonlyMap<AtivoPortfolioSubview, SectionId> = new Map([
  ['visao-geral', 'overview'],
  ['imoveis-composicao', 'realEstateComposition'],
  ['imoveis-risco', 'realEstateRisk'],
  ['imoveis-contratos', 'realEstateContracts'],
  ['fundos-investidos', 'funds'],
  ['ativos-financeiros', 'financialAssets'],
  ['acoes-participacoes', 'equities'],
]);

type Props = {
  tickerLabel: string;
  data: PortfolioTabViewModel;
  /**
   * Active subview driven by the URL segment. The component falls back to
   * `visao-geral` when the slug doesn't map to any section visible in the
   * data so deep-links never break.
   */
  activeSubview: AtivoPortfolioSubview;
};

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 2,
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

const SECTION_LABEL: Record<SectionId, string> = {
  overview: 'Visão Geral',
  realEstate: 'Imóveis',
  realEstateComposition: 'Imóveis/Composição',
  realEstateRisk: 'Imóveis/Risco',
  realEstateContracts: 'Imóveis/Contratos',
  funds: 'Fundos Investidos',
  financialAssets: 'Ativos Financeiros',
  equities: 'Ações/Participações',
};

const metricAccentColor = (label: string, colors: ReturnType<typeof useAppColors>) => {
  const key = label.toLowerCase();
  if (key.includes('patrimônio') || key.includes('total investido')) {
    return colors.charts.portfolioMix.realEstate;
  }
  if (key.includes('disponibilidades') || key.includes('receber')) {
    return colors.charts.portfolioMix.funds;
  }
  if (key.includes('passivo')) {
    return colors.semantic.dangerStrong;
  }
  return colors.semantic.violet;
};

const metricInfoText = (label: string) => {
  const key = normalizeMetricLabel(label);
  if (key.includes('valor do ativo')) {
    return 'Total contábil do ativo reportado no informe mensal complementar da CVM.';
  }
  if (key.includes('a pagar') || key.includes('total passivo')) {
    return 'Total de obrigações reportadas no informe mensal de ativo/passivo da CVM.';
  }
  if (key.includes('patrimonio liquido')) {
    return 'Patrimônio líquido reportado pela CVM, após considerar ativo e passivo.';
  }
  if (key.includes('total investido')) {
    return 'Subtotal de ativos investidos informado pela CVM; componente do ativo total.';
  }
  return 'Indicador calculado a partir da base CVM do período mais recente.';
};

const formatMetricValue = (value: number | null, kind: MetricKind) => {
  if (value === null) return '-';
  if (kind === 'currency') return CURRENCY_FORMATTER.format(value);
  if (kind === 'percent') return `${PERCENT_FORMATTER.format(value)}%`;
  return NUMBER_FORMATTER.format(value);
};

const formatOptionalCurrency = (value: number | null) => (value === null ? '-' : CURRENCY_FORMATTER.format(value));

const formatCompactCurrency = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000_000)} bi`;
  }
  if (abs >= 1_000_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000)} mi`;
  }
  if (abs >= 1_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000)} mil`;
  }
  return CURRENCY_FORMATTER.format(value);
};

const formatOptionalNumber = (value: number | null) => (value === null ? '-' : NUMBER_FORMATTER.format(value));

const formatOptionalPercent = (value: number | null) => (value === null ? '-' : `${PERCENT_FORMATTER.format(value)}%`);

const formatDateOrDash = (value: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : DATE_FORMATTER.format(parsed);
};

const formatPercentFromFraction = (value: number) => `${PERCENT_FORMATTER.format(value)}%`;
const normalizeMetricLabel = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const groupCount = (labels: string[]) => {
  const map = new Map<string, number>();
  for (const label of labels) {
    map.set(label, (map.get(label) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
};

const groupSum = (items: Array<{ label: string; value: number | null }>) => {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.label, (map.get(item.label) ?? 0) + (item.value ?? 0));
  }
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
};

export function TickerPortfolioTabClient({ tickerLabel, data, activeSubview }: Props) {
  const theme = useTheme();
  const colors = useAppColors();
  const router = useRouter();
  const pathname = usePathname();
  const isDark = theme.palette.mode === 'dark';
  const chartColors = useMemo(
    () => [
      colors.charts.portfolioMix.realEstate,
      colors.charts.portfolioMix.funds,
      colors.charts.portfolioMix.financialAssets,
      colors.charts.portfolioMix.equities,
      colors.semantic.dangerStrong,
      colors.semantic.cyan,
    ],
    [colors],
  );
  const availableSections: SectionId[] = useMemo(() => {
    const sections: SectionId[] = ['overview'];
    if (data.realEstate.length > 0) {
      sections.push('realEstateComposition');
    }
    if (data.realEstate.length > 0) {
      sections.push('realEstateRisk');
    }
    if (data.contractBreakdown.length > 0 || data.contracts.length > 0) {
      sections.push('realEstateContracts');
    }
    if (data.funds.length > 0) sections.push('funds');
    if (data.financialAssets.length > 0 || data.maturityBreakdown.length > 0 || data.indexerBreakdown.length > 0) {
      sections.push('financialAssets');
    }
    if (data.relatedEquities.length > 0) sections.push('equities');
    return sections;
  }, [
    data.contractBreakdown.length,
    data.contracts.length,
    data.financialAssets.length,
    data.funds.length,
    data.indexerBreakdown.length,
    data.maturityBreakdown.length,
    data.realEstate.length,
    data.relatedEquities.length,
  ]);
  const activeSection: SectionId = SUBVIEW_TO_SECTION.get(activeSubview) ?? 'overview';
  const activeSectionIsAvailable = availableSections.includes(activeSection);
  const visibleActiveSection: SectionId = activeSectionIsAvailable ? activeSection : 'overview';
  const subviewHref = (subview: AtivoPortfolioSubview) =>
    ativoPath.withActivePortfolioSubview({
      ticker: tickerLabel,
      tab: 'carteira',
      portfolioSubview: subview,
    });
  const resolveCompositionTargetSection = (item: CompositionItem) => {
    const label = item.label.trim().toLowerCase();
    if (label === 'imóveis e direitos reais' || label === 'imoveis e direitos reais') {
      return 'realEstateComposition' as const;
    }
    if (item.targetSection === 'realEstate') return 'realEstateComposition';
    return item.targetSection;
  };
  const resolveCompositionHref = (item: CompositionItem) => {
    const target = resolveCompositionTargetSection(item);
    if (!target) return null;
    const slug = SECTION_TO_SUBVIEW[target];
    const availableTarget = availableSections.includes(target)
      ? slug
      : SECTION_TO_SUBVIEW[availableSections[0] ?? 'overview'];
    return subviewHref(availableTarget);
  };
  const isCashCompositionItem = (label: string) => {
    const normalized = label.trim().toLowerCase();
    return normalized === 'caixa e liquidez' || normalized === 'valores a receber' || normalized === 'valores a pagar';
  };
  const goToCashTab = () => {
    const safePathname = pathname ?? '';
    // Trim any subview segment before appending /caixa so the
    // canonical caixa overview is selected.
    const base = safePathname.split('/').slice(0, 3).join('/');
    router.replace(`${base}/caixa`);
  };
  const compositionAbsTotal = useMemo(
    () => data.composition.reduce((acc, item) => acc + Math.abs(item.value), 0),
    [data.composition],
  );
  const compositionWithColor = useMemo(
    () =>
      [...data.composition]
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
        .map((item) => ({
          ...item,
          absValue: Math.abs(item.value),
          isNegative: item.value < 0,
        })),
    [data.composition],
  );
  const compositionPositiveItems = useMemo(
    () =>
      compositionWithColor
        .filter((item) => !item.isNegative)
        .map((item, index) => ({
          ...item,
          color: chartColors[index % chartColors.length],
        })),
    [chartColors, compositionWithColor],
  );
  const compositionNegativeItems = useMemo(
    () =>
      compositionWithColor
        .filter((item) => item.isNegative)
        .map((item, index) => ({
          ...item,
          color: [
            colors.semantic.danger,
            colors.semantic.dangerStrong,
            colors.semantic.warningStrong,
            colors.semantic.slate,
          ][index % 4],
        })),
    [colors, compositionWithColor],
  );
  const compositionPositiveTotal = useMemo(
    () => compositionPositiveItems.reduce((acc, item) => acc + item.absValue, 0),
    [compositionPositiveItems],
  );
  const compositionNegativeTotal = useMemo(
    () => compositionNegativeItems.reduce((acc, item) => acc + item.absValue, 0),
    [compositionNegativeItems],
  );
  const compositionDonutBackground = useMemo(() => {
    if (compositionPositiveItems.length === 0 || compositionPositiveTotal <= 0) {
      return 'hsl(var(--muted))';
    }
    let progress = 0;
    const slices = compositionPositiveItems.map((item) => {
      const fraction = item.absValue / compositionPositiveTotal;
      const start = progress * 100;
      progress += fraction;
      const end = progress * 100;
      return `${item.color} ${start}% ${end}%`;
    });
    return `conic-gradient(${slices.join(', ')})`;
  }, [compositionPositiveItems, compositionPositiveTotal]);
  const compositionNegativeDonutBackground = useMemo(() => {
    if (compositionNegativeItems.length === 0 || compositionNegativeTotal <= 0) {
      return 'hsl(var(--muted))';
    }
    let progress = 0;
    const slices = compositionNegativeItems.map((item) => {
      const fraction = item.absValue / compositionNegativeTotal;
      const start = progress * 100;
      progress += fraction;
      const end = progress * 100;
      return `${item.color} ${start}% ${end}%`;
    });
    return `conic-gradient(${slices.join(', ')})`;
  }, [compositionNegativeItems, compositionNegativeTotal]);
  const contractDonutBackground = useMemo(() => {
    if (data.contractBreakdown.length === 0) return 'hsl(var(--muted))';
    let progress = 0;
    const slices = data.contractBreakdown.map((item, index) => {
      const color = chartColors[index % chartColors.length];
      const start = progress * 100;
      progress += item.percent / 100;
      const end = progress * 100;
      return `${color} ${start}% ${end}%`;
    });
    return `conic-gradient(${slices.join(', ')})`;
  }, [chartColors, data.contractBreakdown]);

  const content = (() => {
    if (visibleActiveSection === 'overview') {
      return (
        <PortfolioOverviewSection
          data={data}
          isDark={isDark}
          compositionPositiveItems={compositionPositiveItems}
          compositionNegativeItems={compositionNegativeItems}
          compositionPositiveTotal={compositionPositiveTotal}
          compositionNegativeTotal={compositionNegativeTotal}
          compositionAbsTotal={compositionAbsTotal}
          compositionDonutBackground={compositionDonutBackground}
          compositionNegativeDonutBackground={compositionNegativeDonutBackground}
          onGoToCashTab={goToCashTab}
          resolveCompositionHref={resolveCompositionHref}
          isCashCompositionItem={isCashCompositionItem}
          metricAccentColor={metricAccentColor}
          appColors={colors}
          metricInfoText={metricInfoText}
          normalizeMetricLabel={normalizeMetricLabel}
          formatMetricValue={formatMetricValue}
          formatCompactCurrency={formatCompactCurrency}
          formatOptionalCurrency={formatOptionalCurrency}
          formatPercentFromFraction={formatPercentFromFraction}
        />
      );
    }

    if (
      visibleActiveSection === 'realEstate' ||
      visibleActiveSection === 'realEstateComposition' ||
      visibleActiveSection === 'realEstateRisk' ||
      visibleActiveSection === 'realEstateContracts'
    ) {
      return (
        <PortfolioRealEstateSection
          data={data}
          activeSection={visibleActiveSection}
          chartColors={chartColors}
          isDark={isDark}
          contractDonutBackground={contractDonutBackground}
          groupCount={groupCount}
          groupSum={groupSum}
          formatOptionalPercent={formatOptionalPercent}
        />
      );
    }

    if (visibleActiveSection === 'funds') {
      return (
        <PortfolioFundsSection
          data={data}
          chartColors={chartColors}
          groupCount={groupCount}
          groupSum={groupSum}
          formatOptionalNumber={formatOptionalNumber}
          formatOptionalCurrency={formatOptionalCurrency}
          formatOptionalPercent={formatOptionalPercent}
        />
      );
    }

    if (visibleActiveSection === 'financialAssets') {
      return (
        <PortfolioFinancialAssetsSection
          data={data}
          chartColors={chartColors}
          groupCount={groupCount}
          groupSum={groupSum}
          formatDateOrDash={formatDateOrDash}
          formatOptionalNumber={formatOptionalNumber}
          formatOptionalCurrency={formatOptionalCurrency}
        />
      );
    }

    return (
      <PortfolioEquitiesSection
        data={data}
        chartColors={chartColors}
        groupCount={groupCount}
        groupSum={groupSum}
        formatOptionalNumber={formatOptionalNumber}
        formatOptionalCurrency={formatOptionalCurrency}
      />
    );
  })();
  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
        <Paper variant='outlined' sx={SIDEBAR_TAB_PAPER_SX}>
          <Typography variant='overline' sx={SIDEBAR_TAB_HEADER_SX}>
            Carteira
          </Typography>
          <Stack direction='column' spacing={0.5}>
            {availableSections.map((section) => {
              const isActive = visibleActiveSection === section && activeSectionIsAvailable;
              return (
                <AppLink
                  key={section}
                  href={subviewHref(SECTION_TO_SUBVIEW[section])}
                  underline='none'
                  sx={{
                    ...sidebarTabButtonSx(isActive),
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'left',
                  }}
                >
                  {SECTION_LABEL[section]}
                </AppLink>
              );
            })}
          </Stack>
        </Paper>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {visibleActiveSection === 'overview' ? (
            <Stack spacing={2}>
              <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
                <Typography variant='h6' fontWeight={800}>
                  Carteira de {tickerLabel}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Fonte: {data.sourceLabel} | Referência: {formatDateOrDash(data.referenceDate)} | CNPJ:{' '}
                  {data.cnpjClasse ?? '-'}
                </Typography>
                <Stack direction='row' spacing={0.7} useFlexGap flexWrap='wrap' sx={{ mt: 0.8 }}>
                  <Tooltip title='Data da base mensal de ativo/passivo usada para caixa, receber e pagar.' arrow>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`Ref. mensal A/P: ${formatDateOrDash(data.referenceStamps.mensalAtivoPassivo)}`}
                    />
                  </Tooltip>
                  <Tooltip
                    title='Data da base mensal complementar usada para valor do ativo e patrimônio líquido.'
                    arrow
                  >
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`Ref. mensal compl.: ${formatDateOrDash(data.referenceStamps.mensalComplemento)}`}
                    />
                  </Tooltip>
                  <Tooltip title='Datas das últimas bases trimestrais por bloco de dados.' arrow>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={`Ref. trim. (imóveis/ativos/contratos): ${formatDateOrDash(
                        data.referenceStamps.trimestralImoveis,
                      )} / ${formatDateOrDash(data.referenceStamps.trimestralAtivos)} / ${formatDateOrDash(
                        data.referenceStamps.trimestralContratos,
                      )}`}
                    />
                  </Tooltip>
                </Stack>
              </Paper>
              {content}
            </Stack>
          ) : (
            content
          )}
        </Box>
      </Stack>

      {data.notes.length > 0 ? (
        <Paper variant='outlined' sx={{ p: 1.5 }}>
          <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 0.5 }}>
            Transparência de dados
          </Typography>
          <Stack spacing={0.4}>
            {data.notes.map((note) => (
              <Typography key={note} variant='body2' color='text.secondary'>
                - {note}
              </Typography>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
